import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, FileText, MailQuestion, UsersRound, Settings, LogOut, Bell, Building2,
  ChevronRight,
} from 'lucide-react';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const NAV = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/admin/leads', label: 'Leads', icon: Users },
  { to: '/admin/applications', label: 'Applications', icon: FileText },
  { to: '/admin/contacts', label: 'Contact Messages', icon: MailQuestion },
  { to: '/admin/team', label: 'Team Members', icon: UsersRound, ownerOnly: true },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
];

export default function Admin() {
  const { user, loading, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-pulse text-slate-400">Loading…</div></div>;
  if (!user) return <Navigate to="/admin/login" replace state={{ from: location }} />;

  const handleLogout = async () => {
    await logout();
    toast.success('Signed out');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#f6f7fb]" data-testid="admin-shell">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center p-1 shadow-sm">
              <img src="/assets/logo-white.png" alt="Elite Roofing" className="h-full w-full object-contain" />
            </div>
            <div className="hidden sm:block">
              <div className="text-sm font-bold text-slate-900 leading-tight">Elite Roofing &amp; Construction</div>
              <div className="text-xs text-slate-500 leading-tight">Admin Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button className="relative h-10 w-10 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid="admin-notifications" aria-label="Notifications">
              <Bell className="h-5 w-5" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 h-10 pl-1 pr-3 rounded-full hover:bg-slate-100 transition-colors" data-testid="admin-user-menu">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#0A2540] to-[#13294a] text-white flex items-center justify-center text-sm font-bold">
                    {(user.name || user.email || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-semibold text-slate-900 leading-tight">{user.name}</div>
                    <div className="text-xs text-slate-500 leading-tight capitalize">{user.role}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/admin/settings')}><Settings className="h-4 w-4 mr-2" /> Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="admin-logout"><LogOut className="h-4 w-4 mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Body: sidebar + content */}
      <div className="flex">
        <aside className="hidden lg:flex flex-col w-64 shrink-0 min-h-[calc(100vh-4rem)] bg-white border-r border-slate-200" data-testid="admin-sidebar">
          <nav className="flex-1 p-3 space-y-1">
            {NAV.filter((n) => !n.ownerOnly || user.role === 'owner').map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) =>
                `flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-[#0A2540] text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`
              } data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}>
                {({ isActive }) => (
                  <>
                    <span className="flex items-center gap-2.5"><n.icon className="h-4 w-4" />{n.label}</span>
                    {isActive && <ChevronRight className="h-4 w-4" />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-slate-200">
            <p className="text-[11px] text-slate-400 leading-tight">Licensed Florida General Contractor</p>
            <p className="text-[11px] text-slate-500 font-semibold">CGC032777</p>
          </div>
        </aside>
        <main className="flex-1 min-w-0">
          {/* Mobile nav (horizontal scroll) */}
          <div className="lg:hidden bg-white border-b border-slate-200 overflow-x-auto">
            <div className="flex gap-1 px-3 py-2 min-w-max">
              {NAV.filter((n) => !n.ownerOnly || user.role === 'owner').map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-1.5 rounded-md text-sm font-medium ${isActive ? 'bg-[#0A2540] text-white' : 'text-slate-600 hover:bg-slate-100'}`
                }>{n.label}</NavLink>
              ))}
            </div>
          </div>
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
